import * as XLSX from "xlsx";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import html2canvas from "html2canvas";
import { fmtEUR, fmtPct } from "./format";

async function captureChartPNG() {
  const el = document.querySelector('[data-testid="chart-container"]');
  if (!el) return null;
  try {
    const canvas = await html2canvas(el, {
      backgroundColor: "#ffffff",
      scale: 2,
      logging: false,
      useCORS: true,
    });
    return { dataUrl: canvas.toDataURL("image/png"), width: canvas.width, height: canvas.height };
  } catch (e) {
    console.warn("Chart capture failed:", e);
    return null;
  }
}

const PRATICA_SOGLIA_FATTIBILITA = 200000;
const SPESE_FATTIBILITA_MSG = "I costi della pratica sono da considerarsi previa fattibilità (investimento superiore a 200.000€ — quotazione caso per caso).";

const PRATICA_FASCE = [
  { max: 20000, label: "0-20k€", gse: 1200, perizia: 1200, revisore: 900 },
  { max: 50000, label: "20-50k€", gse: 1400, perizia: 1800, revisore: 1000 },
  { max: 100000, label: "50-100k€", gse: 1700, perizia: 2700, revisore: 1800 },
  { max: 200000, label: "100-200k€", gse: 1900, perizia: 4200, revisore: 2100 },
];

function computeSpese(result) {
  const investimento = result.input.investimento || 0;
  if (investimento > PRATICA_SOGLIA_FATTIBILITA) {
    return { fattibilita: true };
  }
  const beneficio = result.beneficio_iperammortamento || 0;
  const fascia = PRATICA_FASCE.find((f) => beneficio <= f.max) || PRATICA_FASCE[PRATICA_FASCE.length - 1];
  const sabatini_pratica = result.sabatini_beneficio > 0 ? 350 + result.sabatini_beneficio * 0.07 : 0;
  const totale = fascia.gse + fascia.perizia + fascia.revisore + sabatini_pratica;
  const netto = result.vantaggio_complessivo - totale;
  return { fattibilita: false, fascia, sabatini_pratica, totale, netto };
}

export async function exportToExcel(result, filename = "iperammortamento-2026.xlsx") {
  const wb = XLSX.utils.book_new();
  const spese = computeSpese(result);
  const hasBreakdown = Array.isArray(result.piani_per_bene) && result.piani_per_bene.length > 0;

  // Sheet Riepilogo
  const summary = [
    ["IPERAMMORTAMENTO 2026 - Riepilogo"],
    [],
    ["Dati investimento"],
    ["Importo investimento", result.input.investimento],
    ["Aliquota ammortamento (media)", result.input.aliquota_ammortamento],
    ["Aliquota IRES/IRPEF", result.input.aliquota_ires],
    ["Aliquota IRAP", result.input.aliquota_irap || 0],
    ["Anno di inizio", result.input.anno_inizio],
    ["Tipologia", result.input.tipologia],
    ["Nuova Sabatini", result.input.sabatini],
    ["Importo Sabatini applicato", result.sabatini_base_utilizzato],
    ["Dettaglio per singolo bene", hasBreakdown ? "SÌ" : "NO"],
    [],
    ["Risultati"],
    ["Maggiorazione totale", result.maggiorazione_totale],
    ["Detrazione ordinaria (IRES+IRAP)", result.detrazione_ordinaria_totale || 0],
    ["Beneficio iperammortamento", result.beneficio_iperammortamento],
    ["% beneficio iper. su investimento", result.beneficio_iperammortamento_pct],
    ["Nuova Sabatini (beneficio)", result.sabatini_beneficio],
    ["Investimento netto (post Sabatini)", result.investimento_netto],
    ["Vantaggio complessivo", result.vantaggio_complessivo],
    ["% vantaggio complessivo", result.vantaggio_complessivo_pct],
    ["Investimento effettivo netto", result.investimento_effettivo_netto],
    [],
    ...(spese.fattibilita
      ? [
          ["Spese pratica"],
          [SPESE_FATTIBILITA_MSG],
        ]
      : [
          [`Spese pratica (fascia ${spese.fascia.label})`],
          ["Presentazione e gestione portale GSE", spese.fascia.gse],
          ["Costo tecnico perizia", spese.fascia.perizia],
          ["Corso revisore + visto di conformità", spese.fascia.revisore],
          ...(spese.sabatini_pratica > 0 ? [["Presentazione pratica Nuova Sabatini (€350 + 7%)", spese.sabatini_pratica]] : []),
          ["Totale spese pratica", spese.totale],
          ["Vantaggio netto stimato", spese.netto],
        ]),
  ];
  XLSX.utils.book_append_sheet(wb, XLSX.utils.aoa_to_sheet(summary), "Riepilogo");

  // Sheet Grafico: dati pronti per generare un grafico a colonne in Excel
  // (SheetJS free non supporta l'inserimento di immagini/grafici embed; per questo
  // forniamo i dati ordinati con intestazioni facilmente selezionabili)
  const graficoData = hasBreakdown
    ? (() => {
        const beniLabels = result.piani_per_bene.map((pb) => pb.label);
        const header = ["Anno", ...beniLabels, "Totale vantaggio"];
        const rows = result.piano_ammortamento.map((r) => {
          const perAsset = beniLabels.map((label) => {
            const pb = result.piani_per_bene.find((x) => x.label === label);
            const yr = pb ? pb.piano.find((yy) => yy.anno === r.anno) : null;
            return yr ? yr.vantaggio_fiscale : 0;
          });
          return [r.anno, ...perAsset, r.vantaggio_fiscale];
        });
        return [header, ...rows];
      })()
    : [
        ["Anno", "Vantaggio fiscale annuo"],
        ...result.piano_ammortamento.map((r) => [r.anno, r.vantaggio_fiscale]),
      ];
  const graficoSheet = XLSX.utils.aoa_to_sheet([
    ["Dati per grafico vantaggio fiscale"],
    ["Per creare il grafico: seleziona l'intervallo e vai su Inserisci > Grafico > Istogramma a colonne."],
    [],
    ...graficoData,
  ]);
  XLSX.utils.book_append_sheet(wb, graficoSheet, "Grafico");

  // Sheet: Dettaglio per bene (elenco beni)
  if (hasBreakdown) {
    const beni = [
      ["Bene", "Importo €", "Aliquota %", "Durata anni", "Amm. Ordinario tot.", "Iperammortamento tot.", "Detrazione ordinaria tot.", "Vantaggio fiscale tot."],
      ...result.piani_per_bene.map((pb) => {
        const totOrd = pb.piano.reduce((s, r) => s + r.ammortamento_ordinario, 0);
        const totIper = pb.piano.reduce((s, r) => s + r.iperammortamento, 0);
        const totDet = pb.piano.reduce((s, r) => s + (r.detrazione_ordinaria || 0), 0);
        const totVant = pb.piano.reduce((s, r) => s + r.vantaggio_fiscale, 0);
        return [pb.label, pb.importo, pb.aliquota, pb.piano.length, totOrd, totIper, totDet, totVant];
      }),
    ];
    XLSX.utils.book_append_sheet(wb, XLSX.utils.aoa_to_sheet(beni), "Beni");
  }

  // Sheet Piano aggregato
  const piano = [
    ["Anno", "Aliquota", "Amm. Ordinario", "Iperammortamento", "Amm. Complessivo", "Detrazione ordinaria", "Vantaggio Fiscale"],
    ...result.piano_ammortamento.map((r) => [
      r.anno,
      r.aliquota,
      r.ammortamento_ordinario,
      r.iperammortamento,
      r.ammortamento_complessivo,
      r.detrazione_ordinaria || 0,
      r.vantaggio_fiscale,
    ]),
  ];
  XLSX.utils.book_append_sheet(wb, XLSX.utils.aoa_to_sheet(piano), "Piano aggregato");

  // Sheet per ogni bene (solo se breakdown)
  if (hasBreakdown) {
    result.piani_per_bene.forEach((pb, idx) => {
      const beneData = [
        [`${pb.label} - ${fmtEUR(pb.importo)} @ ${fmtPct(pb.aliquota, 2)}`],
        [],
        ["Anno", "Aliquota", "Amm. Ordinario", "Iperammortamento", "Amm. Complessivo", "Detrazione ordinaria", "Vantaggio Fiscale"],
        ...pb.piano.map((r) => [
          r.anno,
          r.aliquota,
          r.ammortamento_ordinario,
          r.iperammortamento,
          r.ammortamento_complessivo,
          r.detrazione_ordinaria || 0,
          r.vantaggio_fiscale,
        ]),
      ];
      const safeName = `Bene ${idx + 1}`.slice(0, 31);
      XLSX.utils.book_append_sheet(wb, XLSX.utils.aoa_to_sheet(beneData), safeName);
    });
  }

  XLSX.writeFile(wb, filename);
}

export async function exportToPDF(result, filename = "iperammortamento-2026.pdf") {
  const doc = new jsPDF({ unit: "pt", format: "a4" });
  const spese = computeSpese(result);
  const marginX = 40;
  const hasBreakdown = Array.isArray(result.piani_per_bene) && result.piani_per_bene.length > 0;
  let y = 50;

  // Cattura grafico dal DOM (deve avvenire mentre il grafico è visibile)
  const chartImg = await captureChartPNG();

  doc.setFont("helvetica", "bold");
  doc.setFontSize(18);
  doc.text("Iperammortamento 2026", marginX, y);
  y += 8;
  doc.setDrawColor(26, 171, 103);
  doc.setLineWidth(2);
  doc.line(marginX, y, marginX + 60, y);
  y += 24;

  doc.setFont("helvetica", "normal");
  doc.setFontSize(10);
  doc.setTextColor(92, 88, 82);
  doc.text("Simulazione benefici fiscali - L. 199/2025 (Iperammortamento 2026)", marginX, y);
  y += 24;

  // Input recap
  autoTable(doc, {
    startY: y,
    head: [["Dati investimento", "Valore"]],
    body: [
      ["Importo investimento", fmtEUR(result.input.investimento)],
      ["Aliquota ammortamento (media)", `${(result.input.aliquota_ammortamento * 100).toFixed(2)}%`],
      ["Aliquota IRES/IRPEF", `${(result.input.aliquota_ires * 100).toFixed(2)}%`],
      ["Aliquota IRAP", `${((result.input.aliquota_irap || 0) * 100).toFixed(2)}%`],
      ["Anno di inizio", `${result.input.anno_inizio}`],
      ["Tipologia", result.input.tipologia === "industria_4_0" ? "Industria 4.0" : "Transizione ecologica"],
      ["Nuova Sabatini", result.input.sabatini],
      ["Base Sabatini utilizzata", fmtEUR(result.sabatini_base_utilizzato)],
      ["Dettaglio per singolo bene", hasBreakdown ? "SÌ" : "NO"],
    ],
    theme: "grid",
    headStyles: { fillColor: [10, 10, 10], textColor: 255 },
    styles: { font: "helvetica", fontSize: 9 },
    margin: { left: marginX, right: marginX },
  });

  autoTable(doc, {
    head: [["Risultati", "Valore"]],
    body: [
      ["Maggiorazione totale", fmtEUR(result.maggiorazione_totale)],
      ["Detrazione ordinaria (IRES+IRAP)", fmtEUR(result.detrazione_ordinaria_totale || 0)],
      ["Beneficio iperammortamento", fmtEUR(result.beneficio_iperammortamento)],
      ["Nuova Sabatini (beneficio)", fmtEUR(result.sabatini_beneficio)],
      ["Investimento netto", fmtEUR(result.investimento_netto)],
      [
        { content: "Vantaggio complessivo", styles: { fontStyle: "bold" } },
        { content: fmtEUR(result.vantaggio_complessivo), styles: { fontStyle: "bold" } },
      ],
      ["% vantaggio complessivo", fmtPct(result.vantaggio_complessivo_pct)],
      ["Investimento effettivo netto", fmtEUR(result.investimento_effettivo_netto)],
    ],
    theme: "grid",
    headStyles: { fillColor: [26, 171, 103], textColor: 255 },
    styles: { font: "helvetica", fontSize: 9 },
    margin: { left: marginX, right: marginX },
  });

  // Elenco beni (solo se breakdown)
  if (hasBreakdown) {
    autoTable(doc, {
      head: [["Bene", "Importo", "Aliquota", "Anni", "Amm.Ord.", "Iper", "Vantaggio"]],
      body: result.piani_per_bene.map((pb) => {
        const totOrd = pb.piano.reduce((s, r) => s + r.ammortamento_ordinario, 0);
        const totIper = pb.piano.reduce((s, r) => s + r.iperammortamento, 0);
        const totVant = pb.piano.reduce((s, r) => s + r.vantaggio_fiscale, 0);
        return [
          pb.label,
          fmtEUR(pb.importo),
          `${(pb.aliquota * 100).toFixed(2)}%`,
          `${pb.piano.length}`,
          fmtEUR(totOrd),
          fmtEUR(totIper),
          fmtEUR(totVant),
        ];
      }),
      theme: "striped",
      headStyles: { fillColor: [26, 171, 103], textColor: 255 },
      styles: { font: "helvetica", fontSize: 8 },
      margin: { left: marginX, right: marginX },
    });
  }

  if (spese.fattibilita) {
    autoTable(doc, {
      head: [["Spese pratica", ""]],
      body: [
        [
          {
            content: SPESE_FATTIBILITA_MSG,
            colSpan: 2,
            styles: { fontStyle: "italic", textColor: [26, 171, 103] },
          },
        ],
      ],
      theme: "grid",
      headStyles: { fillColor: [26, 171, 103], textColor: 255 },
      styles: { font: "helvetica", fontSize: 9 },
      margin: { left: marginX, right: marginX },
    });
  } else {
    autoTable(doc, {
      head: [[`Spese pratica (fascia ${spese.fascia.label})`, "Importo"]],
      body: [
        ["Presentazione e gestione portale GSE", fmtEUR(spese.fascia.gse)],
        ["Costo tecnico perizia", fmtEUR(spese.fascia.perizia)],
        ["Corso revisore + visto di conformità", fmtEUR(spese.fascia.revisore)],
        ...(spese.sabatini_pratica > 0
          ? [["Presentazione pratica Nuova Sabatini (€350 + 7%)", fmtEUR(spese.sabatini_pratica)]]
          : []),
        [{ content: "Totale spese pratica", styles: { fontStyle: "bold" } }, { content: fmtEUR(spese.totale), styles: { fontStyle: "bold" } }],
        [
          { content: "Vantaggio netto stimato (vantaggio - spese)", styles: { fontStyle: "bold", textColor: [26, 171, 103] } },
          { content: fmtEUR(spese.netto), styles: { fontStyle: "bold", textColor: [26, 171, 103], fontSize: 11 } },
        ],
      ],
      theme: "grid",
      headStyles: { fillColor: [26, 171, 103], textColor: 255 },
      styles: { font: "helvetica", fontSize: 9 },
      margin: { left: marginX, right: marginX },
    });
  }

  // Grafico (immagine) prima del piano ammortamento
  if (chartImg) {
    const pageWidthPt = doc.internal.pageSize.width;
    const maxW = pageWidthPt - marginX * 2;
    const aspect = chartImg.height / chartImg.width;
    const imgW = maxW;
    const imgH = imgW * aspect;
    let startY = doc.lastAutoTable ? doc.lastAutoTable.finalY + 20 : y;
    if (startY + imgH > doc.internal.pageSize.height - 60) {
      doc.addPage();
      startY = 60;
    }
    doc.setFont("helvetica", "bold");
    doc.setFontSize(11);
    doc.setTextColor(10, 10, 10);
    doc.text(hasBreakdown ? "Grafico vantaggio fiscale per bene" : "Grafico vantaggio fiscale annuale", marginX, startY);
    startY += 12;
    doc.addImage(chartImg.dataUrl, "PNG", marginX, startY, imgW, imgH);
    // update the "cursor" position by inserting an empty table for spacing
    autoTable(doc, { startY: startY + imgH + 10, head: [], body: [], theme: "plain", margin: { left: marginX, right: marginX } });
  }

  // Piano aggregato
  autoTable(doc, {
    head: [[
      hasBreakdown ? "Piano ammortamento aggregato" : "Piano ammortamento",
      "", "", "", "", "", "",
    ]],
    body: [],
    theme: "plain",
    styles: { font: "helvetica", fontSize: 11, fontStyle: "bold", textColor: [10, 10, 10] },
    margin: { left: marginX, right: marginX },
  });

  autoTable(doc, {
    head: [["Anno", "Aliquota", "Amm. Ordinario", "Iperammortamento", "Amm. Complessivo", "Detr. ordinaria", "Vantaggio Fiscale"]],
    body: result.piano_ammortamento.map((r) => [
      r.anno,
      `${(r.aliquota * 100).toFixed(2)}%`,
      fmtEUR(r.ammortamento_ordinario),
      fmtEUR(r.iperammortamento),
      fmtEUR(r.ammortamento_complessivo),
      fmtEUR(r.detrazione_ordinaria || 0),
      fmtEUR(r.vantaggio_fiscale),
    ]),
    theme: "striped",
    headStyles: { fillColor: [10, 10, 10], textColor: 255 },
    styles: { font: "helvetica", fontSize: 8 },
    margin: { left: marginX, right: marginX },
  });

  // Piano per singolo bene
  if (hasBreakdown) {
    result.piani_per_bene.forEach((pb) => {
      autoTable(doc, {
        head: [[`Piano ammortamento - ${pb.label} (${fmtEUR(pb.importo)} @ ${(pb.aliquota * 100).toFixed(2)}%)`]],
        body: [],
        theme: "plain",
        styles: { font: "helvetica", fontSize: 10, fontStyle: "bold", textColor: [26, 171, 103] },
        margin: { left: marginX, right: marginX },
      });
      autoTable(doc, {
        head: [["Anno", "Aliquota", "Amm. Ordinario", "Iperammortamento", "Amm. Complessivo", "Detr. ordinaria", "Vantaggio Fiscale"]],
        body: pb.piano.map((r) => [
          r.anno,
          `${(r.aliquota * 100).toFixed(2)}%`,
          fmtEUR(r.ammortamento_ordinario),
          fmtEUR(r.iperammortamento),
          fmtEUR(r.ammortamento_complessivo),
          fmtEUR(r.detrazione_ordinaria || 0),
          fmtEUR(r.vantaggio_fiscale),
        ]),
        theme: "striped",
        headStyles: { fillColor: [10, 10, 10], textColor: 255 },
        styles: { font: "helvetica", fontSize: 8 },
        margin: { left: marginX, right: marginX },
      });
    });
  }

  // Disclaimer finale
  const disclaimer =
    "I valori mostrati sono stime indicative calcolate su coefficienti di legge, aliquote fiscali dichiarate e tariffe medie di mercato per le spese di pratica. " +
    "L'effettivo beneficio dipende dalla situazione fiscale specifica dell'impresa, dalla qualificazione dei beni ai sensi degli Allegati IV e V alla L. 199/2025 e s.m.i., " +
    "dagli esiti della prenotazione GSE e dalla verifica sostanziale dei requisiti. Nessun importo costituisce garanzia di erogazione o consulenza fiscale definitiva. " +
    "Verifica sempre i risultati con il proprio commercialista o con un consulente fiscale prima di assumere impegni contrattuali o presentare pratiche.";

  const pageWidth = doc.internal.pageSize.width;
  const usableWidth = pageWidth - marginX * 2;
  let currentY = doc.lastAutoTable ? doc.lastAutoTable.finalY + 20 : 100;

  if (currentY > doc.internal.pageSize.height - 130) {
    doc.addPage();
    currentY = 60;
  }

  doc.setFontSize(9);
  doc.setFont("helvetica", "bold");
  doc.setTextColor(26, 171, 103);
  doc.text("Disclaimer", marginX, currentY);
  currentY += 12;
  doc.setFont("helvetica", "normal");
  doc.setFontSize(8);
  doc.setTextColor(80);
  const lines = doc.splitTextToSize(disclaimer, usableWidth);
  doc.text(lines, marginX, currentY);

  doc.setFontSize(7);
  doc.setTextColor(150);
  doc.text(
    "Studio Ingegneria Valore · info@studioingegneriavalore.it · 045 5116212",
    marginX,
    doc.internal.pageSize.height - 20,
  );

  doc.save(filename);
}
